<template>
  <div class="project-overview">
    <!-- ====== 管理员/研究员 原有视图 ====== -->
    <template v-if="isAdminOrRd">
    <!-- 第一行：项目信息 + 项目进度 -->
    <div class="row">
      <!-- 项目信息卡片 -->
      <div class="card">
        <div class="card-header">
          <i class="el-icon-folder header-icon blue"></i>
          <span>项目信息</span>
        </div>
        <div class="card-body">
          <div class="info-item">
            <label>项目名称</label>
            <span class="value">{{ project.name || project.Name || '-' }}</span>
          </div>
          <div class="info-item">
            <label>项目代号</label>
            <span class="value mono">{{ project.code || project.Code || '-' }}</span>
          </div>
          <div class="info-item">
            <label>项目时间</label>
            <span class="value">{{ projectTimeRange }}</span>
          </div>
          <div class="info-item">
            <label>项目状态</label>
            <el-tag :type="projectStatus === 1 ? 'success' : 'info'" size="small" effect="plain" class="status-tag">
              {{ projectStatus === 1 ? '进行中' : '已完成' }}
            </el-tag>
          </div>
        </div>
      </div>

      <!-- 项目进度卡片 -->
      <div class="card">
        <div class="card-header">
          <i class="el-icon-time header-icon green"></i>
          <span>项目进度</span>
        </div>
        <div class="card-body">
          <div class="progress-main">
            <div class="progress-big-number" :class="'text-' + riskData.overdueRisk">
              {{ riskData.completionRate }}%
            </div>
            <el-progress 
              :percentage="riskData.completionRate" 
              :stroke-width="10"
              :color="progressColor"
              :show-text="true"
              class="progress-bar"
            ></el-progress>
          </div>
          <div class="time-row">
            <div class="time-cell">
              <label>开始时间</label>
              <span>{{ projectStartTime }}</span>
            </div>
            <div class="time-cell">
              <label>截止时间</label>
              <span>{{ projectEndTime }}</span>
            </div>
            <div class="time-cell">
              <label>剩余天数</label>
              <span :class="{ 'text-danger': riskData.remainingDays <= 7 }">
                <strong>{{ riskData.remainingDays }}</strong> 天
              </span>
            </div>
          </div>
          <div class="risk-bar" :class="'risk-' + riskData.overdueRisk">
            <i :class="riskIcon"></i>
            <span class="risk-text">{{ riskLabel }}</span>
            <span class="risk-divider" v-if="riskData.needSpeedUp">│</span>
            <span class="risk-speed" v-if="riskData.needSpeedUp">
              需加速 {{ riskData.speedUpRatio }}x
            </span>
          </div>
        </div>
      </div>
    </div>


    <!-- 第三行：成员概览 + 波次回收进度 -->
    <div class="row">
      <!-- 成员概览卡片 -->
      <div class="card">
        <div class="card-header">
          <i class="el-icon-user header-icon blue"></i>
          <span>项目成员</span>
          <span class="count-badge">{{ members.length }}人</span>
        </div>
        <div class="card-body">
          <div class="role-stats">
            <div class="role-item" v-for="rs in memberStats" :key="rs.role">
              <span class="role-count">{{ rs.count }}</span>
              <span class="role-label">{{ rs.label }}</span>
            </div>
          </div>
          <div class="member-avatars">
            <div 
              v-for="(m, i) in displayMembers" 
              :key="m.id || i" 
              class="avatar"
              :style="{ background: avatarColors[i % avatarColors.length] }"
              :title="m.name || m.Name || m.username"
            >
              {{ getInitial(m.name || m.Name || m.username) }}
            </div>
            <div v-if="members.length > 5" class="avatar more">
              +{{ members.length - 5 }}
            </div>
          </div>
        </div>
      </div>

      <!-- 波次回收进度卡片 -->
      <div class="card">
        <div class="card-header">
          <i class="el-icon-s-order header-icon orange"></i>
          <span>波次回收进度</span>
        </div>
        <div class="card-body">
          <div class="wave-list">
            <div v-for="wave in waves" :key="wave.id" class="wave-block">
              <div class="wave-block-header">
                <span class="wave-name" :title="wave.name">{{ wave.name }}</span>
                <span class="wave-summary">{{ wave.approvedCount }}/{{ wave.totalExpected }} 已通过</span>
              </div>
              <div class="phase-list">
                <div v-for="ps in wave.phaseStats" :key="ps.phaseNo" class="phase-row">
                  <div class="phase-info">
                    <span class="phase-no">{{ ps.phaseNo }}</span>
                    <span class="phase-name" :title="ps.phaseName">{{ ps.phaseName }}</span>
                  </div>
                  <div class="phase-progress">
                    <el-progress 
                      :percentage="ps.dealerCount > 0 ? Math.min(100, Math.round(ps.approvedCount / ps.dealerCount * 100)) : 0"
                      :stroke-width="6"
                      :color="ps.approvedCount >= ps.dealerCount && ps.dealerCount > 0 ? '#67c23a' : '#409eff'"
                    ></el-progress>
                  </div>
                  <div class="phase-stats">
                    {{ ps.approvedCount }}/{{ ps.dealerCount }}
                  </div>
                </div>
              </div>
            </div>
            <div v-if="waves.length === 0" class="empty-box">
              <i class="el-icon-monitor"></i>
              <p>暂无波次</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 审核概览卡片 -->
    <div class="row audit-summary-row">
      <div class="audit-card">
        <div class="audit-card-icon" style="background: #ecfdf5; color: #10b981;">
          <i class="el-icon-circle-check"></i>
        </div>
        <div class="audit-card-info">
          <div class="audit-card-label">已审定总量</div>
          <div class="audit-card-value">{{ auditSummary.approved }}<span class="unit">份</span></div>
        </div>
      </div>
      <div class="audit-card">
        <div class="audit-card-icon" style="background: #eff6ff; color: #3b82f6;">
          <i class="el-icon-document-checked"></i>
        </div>
        <div class="audit-card-info">
          <div class="audit-card-label">待审核任务</div>
          <div class="audit-card-value">{{ auditSummary.pending }}<span class="unit">个</span></div>
        </div>
      </div>
      <div class="audit-card">
        <div class="audit-card-icon" style="background: #fef3c7; color: #f59e0b;">
          <i class="el-icon-warning"></i>
        </div>
        <div class="audit-card-info">
          <div class="audit-card-label">审核异常</div>
          <div class="audit-card-value">{{ auditSummary.rejected }}<span class="unit">处</span></div>
        </div>
      </div>
    </div>

    <!-- 第四行：数据审核进度（按阶段展示） -->
    <div class="row" v-if="hasAuditData">
      <div class="card full-width">
        <div class="card-header">
          <i class="el-icon-finished header-icon green"></i>
          <span>数据审核进度</span>
        </div>
        <div class="card-body">
          <div class="review-list">
            <div v-for="wave in waves" :key="'review-' + wave.id" class="review-block">
              <div class="review-block-header">
                <span class="wave-name">{{ wave.name }}</span>
                <span class="wave-total">共 {{ wave.totalExpected || 0 }} 份答卷</span>
              </div>
              <div class="phase-audit-list">
                <div v-for="ps in wave.phaseStats" :key="ps.phaseNo" class="phase-audit-block">
                  <div class="phase-audit-header">
                    <span class="phase-no-lg">{{ ps.phaseNo }}</span>
                    <span class="phase-audit-title">{{ ps.phaseName }}</span>
                    <el-tag v-if="ps.formType === 3" size="mini" type="warning">多级审核</el-tag>
                  </div>
                  <div class="review-items">
                    <div class="review-item">
                      <div class="review-label">已提交</div>
                      <div class="review-progress">
                        <el-progress 
                          :percentage="ps.dealerCount > 0 ? Math.round(ps.submittedCount / ps.dealerCount * 100) : 0" 
                          :stroke-width="8" color="#409eff"
                          :show-text="false"
                        ></el-progress>
                      </div>
                      <div class="review-pct">{{ ps.dealerCount > 0 ? Math.round(ps.submittedCount / ps.dealerCount * 100) : 0 }}%</div>
                      <div class="review-num">{{ ps.submittedCount }}/{{ ps.dealerCount }}</div>
                    </div>
                    <div class="review-item">
                      <div class="review-label">{{ ps.formType === 3 ? '一审' : '通过' }}</div>
                      <div class="review-progress">
                        <el-progress 
                          :percentage="ps.dealerCount > 0 ? Math.round(ps.auditReview1 / ps.dealerCount * 100) : 0" 
                          :stroke-width="8" color="#e6a23c"
                          :show-text="false"
                        ></el-progress>
                      </div>
                      <div class="review-pct">{{ ps.dealerCount > 0 ? Math.round(ps.auditReview1 / ps.dealerCount * 100) : 0 }}%</div>
                      <div class="review-num">{{ ps.auditReview1 }}/{{ ps.dealerCount }}</div>
                    </div>
                    <template v-if="ps.formType === 3">
                      <div class="review-item">
                        <div class="review-label">二审</div>
                        <div class="review-progress">
                          <el-progress 
                            :percentage="ps.dealerCount > 0 ? Math.round(ps.auditReview2 / ps.dealerCount * 100) : 0" 
                            :stroke-width="8" color="#409eff"
                            :show-text="false"
                          ></el-progress>
                        </div>
                        <div class="review-pct">{{ ps.dealerCount > 0 ? Math.round(ps.auditReview2 / ps.dealerCount * 100) : 0 }}%</div>
                        <div class="review-num">{{ ps.auditReview2 }}/{{ ps.dealerCount }}</div>
                      </div>
                      <div class="review-item">
                        <div class="review-label">三审</div>
                        <div class="review-progress">
                          <el-progress 
                            :percentage="ps.dealerCount > 0 ? Math.round(ps.auditReview3 / ps.dealerCount * 100) : 0" 
                            :stroke-width="8" color="#67c23a"
                            :show-text="false"
                          ></el-progress>
                        </div>
                        <div class="review-pct">{{ ps.dealerCount > 0 ? Math.round(ps.auditReview3 / ps.dealerCount * 100) : 0 }}%</div>
                        <div class="review-num">{{ ps.auditReview3 }}/{{ ps.dealerCount }}</div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
              <div class="review-block-footer">
                <span class="view-report" @click="$router.push({ name: 'WaveReport', params: { projectId: projectId, waveId: wave.id } })">
                  查看详细报告 <i class="el-icon-arrow-right"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </template>

    <!-- ====== QC 专属视图 ====== -->
    <template v-if="isQcRole">
      <div class="region-view">
        <div class="region-header">
          <h3><i class="el-icon-finished" style="color:#10b981;margin-right:8px;"></i>审核进度概览</h3>
          <div class="region-filters">
            <el-select v-model="selectedWaveId" placeholder="选择波次" size="small" style="width:160px" @change="loadRegionData">
              <el-option v-for="w in waves" :key="w.id" :label="w.name" :value="w.id"></el-option>
            </el-select>
            <el-select v-model="filterDaquId" placeholder="大区" size="small" clearable filterable style="width:120px" @change="filterXiaoquId = null">
              <el-option v-for="d in daquOptions" :key="d.id" :label="d.name" :value="d.id"></el-option>
            </el-select>
            <el-select v-model="filterXiaoquId" placeholder="小区" size="small" clearable filterable style="width:120px">
              <el-option v-for="x in xiaoquOptions" :key="x.id" :label="x.name" :value="x.id"></el-option>
            </el-select>
            <el-select v-model="filterDealerCodes" placeholder="门店" size="small" clearable filterable multiple collapse-tags style="width:200px">
              <el-option v-for="dl in dealerOptions" :key="dl.code" :label="dl.name" :value="dl.code"></el-option>
            </el-select>
            <el-select v-model="filterPhaseStatus" placeholder="阶段状态" size="small" clearable style="width:120px">
              <el-option label="未填写" value="not_started"></el-option>
              <el-option label="填写中" value="in_progress"></el-option>
              <el-option label="已提交" value="submitted"></el-option>
              <el-option label="已通过" value="approved"></el-option>
              <el-option label="已驳回" value="rejected"></el-option>
            </el-select>
          </div>
        </div>
        <div v-loading="regionLoading" class="region-tree">
          <div v-for="daqu in filteredRegionData" :key="daqu.daquId" class="region-daqu">
            <div class="region-row daqu-row" @click="toggleDaqu(daqu.daquId)">
              <i class="el-icon-arrow-right expand-icon" :class="{ open: isDaquOpen(daqu.daquId) }"></i>
              <span class="region-name">{{ daqu.daquName }}</span>
              <span class="region-stat">{{ daqu.dealerCount }} 家门店</span>
              <span class="region-stat success">{{ daqu.approvedCount }} 全通过</span>
              <span class="region-stat primary">{{ daqu.submittedCount }} 已提交</span>
            </div>
            <template v-if="isDaquOpen(daqu.daquId)">
              <div v-for="xq in daqu.xiaoqus" :key="xq.xiaoquId" class="region-xiaoqu">
                <div class="region-row xiaoqu-row" @click="toggleXiaoqu(daqu.daquId + '_' + xq.xiaoquId)">
                  <i class="el-icon-arrow-right expand-icon" :class="{ open: isXiaoquOpen(daqu.daquId + '_' + xq.xiaoquId) }"></i>
                  <span class="region-name">{{ xq.xiaoquName }}</span>
                  <span class="region-stat">{{ xq.dealerCount }} 家</span>
                  <span class="region-stat success">{{ xq.approvedCount }}</span>
                  <span class="region-stat primary">{{ xq.submittedCount }}</span>
                </div>
                <div v-if="isXiaoquOpen(daqu.daquId + '_' + xq.xiaoquId)" class="dealer-list">
                  <el-table :data="xq.dealers" size="mini" class="dealer-table">
                    <el-table-column label="门店编码" prop="dealerCode" width="100"></el-table-column>
                    <el-table-column label="门店名称" prop="dealerName" min-width="140"></el-table-column>
                    <el-table-column label="QC账号" width="100">
                      <template slot-scope="{ row }">
                        <span v-if="row.qcName">{{ row.qcName }}</span>
                        <span v-else style="color:#d1d5db">-</span>
                      </template>
                    </el-table-column>
                    <el-table-column label="阶段进度" min-width="280">
                      <template slot-scope="{ row }">
                        <div class="phase-list">
                          <span v-for="(ps, idx) in (row.phases || [])" :key="idx"
                            class="phase-tag" :class="getPhaseTagClass(ps)">
                            {{ ps.phaseName }}: {{ ps.statusText }}
                          </span>
                          <span v-if="!(row.phases || []).length" style="color:#9ca3af;">—</span>
                        </div>
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
              </div>
            </template>
          </div>
          <div v-if="!regionLoading && filteredRegionData.length === 0" class="empty-box"><i class="el-icon-folder-opened"></i><p>{{ regionData.length === 0 ? '请选择波次' : '无匹配数据' }}</p></div>
        </div>
      </div>
    </template>

    <!-- ====== Ops（执行）专属视图 ====== -->
    <template v-if="isOpsRole">
      <div class="region-view">
        <!-- 汇总统计卡片 -->
        <div class="summary-cards">
          <div class="summary-card">
            <div class="summary-icon" style="background:#eff6ff;color:#3b82f6;"><i class="el-icon-s-order"></i></div>
            <div class="summary-info"><div class="summary-label">分配任务总量</div><div class="summary-value">{{ agentProgressSummary.assignedCount }}<span class="unit">项</span></div></div>
          </div>
          <div class="summary-card">
            <div class="summary-icon" style="background:#ecfdf5;color:#10b981;"><i class="el-icon-circle-check"></i></div>
            <div class="summary-info"><div class="summary-label">全阶段完成</div><div class="summary-value">{{ agentProgressSummary.completedCount }}<span class="unit">项</span></div></div>
          </div>
          <div class="summary-card">
            <div class="summary-icon" style="background:#fef2f2;color:#ef4444;"><i class="el-icon-warning"></i></div>
            <div class="summary-info"><div class="summary-label">待完成</div><div class="summary-value">{{ agentProgressSummary.pendingCount }}<span class="unit">项</span></div></div>
          </div>
          <div class="summary-card">
            <div class="summary-icon" style="background:#f0fdf4;color:#22c55e;"><i class="el-icon-data-analysis"></i></div>
            <div class="summary-info"><div class="summary-label">整体完成率</div><div class="summary-value">{{ agentProgressSummary.progress }}<span class="unit">%</span></div></div>
          </div>
          <div class="summary-card">
            <div class="summary-icon" style="background:#fefce8;color:#eab308;"><i class="el-icon-user"></i></div>
            <div class="summary-info"><div class="summary-label">代理数 / 门店数</div><div class="summary-value">{{ agentProgressSummary.agentCount }}/{{ agentProgressSummary.storeCount }}<span class="unit">个</span></div></div>
          </div>
        </div>
        <div class="region-header">
          <h3><i class="el-icon-user-solid" style="color:#3b82f6;margin-right:8px;"></i>代理进度概览（代理→省份→城市→门店）</h3>
        </div>

        <div class="wave-stats-panel">
          <div class="wave-stats-title">波次门店分配与回收统计</div>
          <el-table :data="displayedWaveStats" size="mini" border>
            <el-table-column label="波次" prop="waveName" min-width="160" />
            <el-table-column label="总门店数" prop="totalStores" width="110" align="center" />
            <el-table-column label="已分配门店" prop="assignedStores" width="120" align="center" />
            <el-table-column label="未分配门店" prop="unassignedStores" width="120" align="center" />
            <el-table-column label="已回收门店(任一阶段提交)" prop="recoveredAssignedStores" min-width="200" align="center" />
            <el-table-column label="分配率" min-width="140">
              <template slot-scope="{ row }">
                <el-progress :percentage="row.totalStores > 0 ? Math.round((row.assignedStores || 0) / row.totalStores * 100) : 0" :stroke-width="10" />
              </template>
            </el-table-column>
            <el-table-column label="回收率(已分配口径)" min-width="170">
              <template slot-scope="{ row }">
                <el-progress :percentage="row.assignedStores > 0 ? Math.round((row.recoveredAssignedStores || 0) / row.assignedStores * 100) : 0" :stroke-width="10" status="success" />
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div class="agent-hierarchy-panel">
          <div class="agent-hierarchy-toolbar">
            <el-select v-model="agentProgressFilters.agentId" placeholder="代理(全部)" clearable filterable size="small" style="width: 180px" @change="handleAgentAgentChange">
              <el-option v-for="a in agentProgressAgentOptions" :key="a.uid" :label="a.name" :value="a.uid" />
            </el-select>
            <el-select v-model="agentProgressFilters.province" placeholder="省份" clearable filterable size="small" style="width: 140px" @change="handleAgentProvinceChange">
              <el-option v-for="p in agentProgressProvinces" :key="p" :label="p" :value="p" />
            </el-select>
            <el-select v-model="agentProgressFilters.city" placeholder="城市" clearable filterable size="small" style="width: 140px" @change="handleAgentCityChange">
              <el-option v-for="c in filteredAgentCities" :key="c" :label="c" :value="c" />
            </el-select>
            <el-select v-model="agentProgressWaveId" placeholder="波次(全部)" clearable filterable size="small" style="width: 180px" @change="handleAgentWaveChange">
              <el-option v-for="w in waves" :key="w.id" :label="w.name" :value="w.id" />
            </el-select>
            <el-select v-model="agentProgressFilters.storeCode" placeholder="门店" clearable filterable size="small" style="width: 180px" @change="loadAgentHierarchyProgress">
              <el-option v-for="s in filteredAgentStores" :key="s.code" :label="s.name" :value="s.code" />
            </el-select>
            <el-input v-model="agentProgressFilters.storeKeyword" placeholder="门店名称/编码" size="small" style="width:200px" clearable @keyup.enter.native="loadAgentHierarchyProgress" @clear="loadAgentHierarchyProgress" />
            <el-button size="small" type="primary" class="ops-toolbar-btn ops-toolbar-btn--primary" @click="loadAgentHierarchyProgress">查询</el-button>
            <el-button size="small" class="ops-toolbar-btn" @click="exportAgentHierarchyProgress">导出进度Excel</el-button>
          </div>

          <!-- 文档 49：Ops 没有归属代理时显示空状态 -->
          <div
            v-if="!agentProgressLoading && (!agentProgressTreeData || agentProgressTreeData.length === 0)"
            class="empty-box" style="padding:40px 0;text-align:center;color:#94a3b8;">
            <i class="el-icon-folder-opened" style="font-size:32px;display:block;margin-bottom:8px;"></i>
            <p>暂无归属到你的代理账号，请先在「项目设置 → 代理管理」中导入。</p>
          </div>

          <el-table
            v-else
            v-loading="agentProgressLoading"
            :data="displayedAgentProgressTreeData"
            row-key="id"
            border
            default-expand-all
            :tree-props="{ children: 'children' }"
            size="mini"
          >
            <el-table-column prop="name" label="节点名称" min-width="240" />
            <el-table-column prop="assignedCount" label="分配任务数" width="120" align="center" />
            <el-table-column prop="completedCount" label="已完成数" width="120" align="center" />
            <el-table-column label="当前进度" min-width="180">
              <template slot-scope="{ row }">
                <el-progress :percentage="row.progress || 0" :stroke-width="12" />
              </template>
            </el-table-column>
            <el-table-column label="波次进度" min-width="360">
              <template slot-scope="{ row }">
                <div v-if="row.waveProgress && row.waveProgress.length" class="wave-progress-chips">
                  <div v-for="wp in row.waveProgress" :key="`${row.id}-${wp.waveId}`" class="wave-progress-chip">
                    <span class="wave-name">{{ wp.waveName }}</span>
                    <span class="wave-num">{{ wp.completedCount }}/{{ wp.assignedCount }}</span>
                    <span class="wave-pct">{{ wp.progress }}%</span>
                  </div>
                </div>
                <span v-else style="color:#9ca3af;">—</span>
              </template>
            </el-table-column>
          </el-table>
        </div>

      </div>
    </template>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import { getProjectDetail, getProjectQuota, getProjectMembers, getAgentHierarchyProgress } from 'index/api/project/manager'
import { getWaveList, getOverviewByRegion } from 'index/api/project/wave'

export default {
  name: 'ProjectOverview',

  data() {
    return {
      loading: false,
      project: {},
      quota: { total: 0, completed: 0, pending: 0, rate: 0 },
      members: [],
      // 文档 51 P2-01：当前用户在该项目下的可见性范围（角色 / isManager / isOps）
      myScope: null,
      waves: [],
      regionData: [],
      regionSummary: null,
      regionLoading: false,
      selectedWaveId: null,
      expandedDaqus: [],
      expandedXiaoqus: [],
      filterDaquId: null,
      filterXiaoquId: null,
      filterDealerCodes: [],
      filterPhaseStatus: null,
      agentProgressFilters: {
        agentId: null,
        province: '',
        city: '',
        storeCode: '',
        storeKeyword: ''
      },
      agentProgressLoading: false,
      agentProgressTreeData: [],
      agentProgressProvinces: [],
      agentProgressCities: [],
      agentProgressWaveStats: [],
      agentProgressWaveId: null,
      // 代理下拉选项缓存：仅在"无代理过滤"的请求成功时刷新；
      // 避免选了某个代理后下拉只剩这一个、第二次切换无法回到全部的问题。
      // key 用 uid（数字），label 用 name 或 username。
      agentProgressAgentOptions: [],
      avatarColors: [
        '#4f8ff7', '#f5a623', '#e74c3c', '#2ecc71', '#9b59b6',
        '#e040fb', '#00bcd4', '#ff7043', '#795548', '#607d8b'
      ]
    }
  },

  computed: {
    projectId() {
      return this.$route.params.projectId
    },
    // 文档 53 P2-01：myScope 未加载时返回 null（不再回退系统角色），
    //   模板用 v-if="isAdminOrRd === true" 等严格判断
    isQcRole() {
      if (this.myScope && typeof this.myScope.role === 'string') {
        const r = this.myScope.role.toLowerCase()
        return r === 'qc' || r === 'reviewer' || this.myScope.role === '质量控制'
      }
      return null
    },
    isOpsRole() {
      if (this.myScope && typeof this.myScope.isOps === 'boolean') return this.myScope.isOps
      return null
    },
    isAdminOrRd() {
      if (this.myScope && typeof this.myScope.isManager === 'boolean') return this.myScope.isManager
      return null
    },
    scopeLoaded() { return !!this.myScope },
    daquOptions() {
      return this.regionData.map(d => ({ id: d.daquId, name: d.daquName }))
    },
    xiaoquOptions() {
      const list = []
      const source = this.filterDaquId
        ? this.regionData.filter(d => d.daquId === this.filterDaquId)
        : this.regionData
      source.forEach(d => (d.xiaoqus || []).forEach(xq => list.push({ id: xq.xiaoquId, name: xq.xiaoquName })))
      return list
    },
    dealerOptions() {
      const list = []
      const source = this.filterDaquId
        ? this.regionData.filter(d => d.daquId === this.filterDaquId)
        : this.regionData
      source.forEach(d => (d.xiaoqus || []).forEach(xq => {
        if (this.filterXiaoquId && xq.xiaoquId !== this.filterXiaoquId) return
        ;(xq.dealers || []).forEach(dl => list.push({ code: dl.dealerCode, name: dl.dealerName || dl.dealerCode }))
      }))
      return list
    },
    filteredAgentCities() {
      // v1.5 修复 P1-03：城市选项从"当前波次展示数据" displayedAgentProgressTreeData 推导，
      // 不再用 agentProgressTreeData（包含其它波次门店），避免切换波次后下拉混入旧波次城市。
      const sourceTree = this.displayedAgentProgressTreeData || []
      if (!this.agentProgressFilters.province) {
        const cities = new Set()
        const walk = (rows = []) => {
          rows.forEach(r => {
            if (r.nodeType === 'store' && r.city) cities.add(r.city)
            if (Array.isArray(r.children)) walk(r.children)
          })
        }
        walk(sourceTree)
        return Array.from(cities)
      }
      const hit = []
      const walk = (rows = []) => {
        rows.forEach(r => {
          if (r.nodeType === 'city' && Array.isArray(r.children) && r.children.some(s => s.province === this.agentProgressFilters.province)) {
            hit.push(r.name)
          }
          if (Array.isArray(r.children)) walk(r.children)
        })
      }
      walk(sourceTree)
      return Array.from(new Set(hit))
    },
    filteredAgentStores() {
      // v1.5 修复 P1-03：门店选项从 displayedAgentProgressTreeData 推导，
      // 切波次后仅展示当前波次的门店。
      const sourceTree = this.displayedAgentProgressTreeData || []
      const stores = []
      const walk = (rows = []) => {
        rows.forEach(r => {
          if (r.nodeType === 'store') {
            const provinceOk = !this.agentProgressFilters.province || r.province === this.agentProgressFilters.province
            const cityOk = !this.agentProgressFilters.city || r.city === this.agentProgressFilters.city
            if (provinceOk && cityOk) {
              stores.push({
                code: r.storeCode || '',
                name: r.name ? `${r.name}${r.storeCode ? `（${r.storeCode}）` : ''}` : (r.storeCode || '未命名门店')
              })
            }
          }
          if (Array.isArray(r.children)) walk(r.children)
        })
      }
      walk(sourceTree)
      const map = new Map()
      stores.forEach(s => {
        if (!s.code) return
        if (!map.has(s.code)) map.set(s.code, s)
      })
      return Array.from(map.values())
    },
    displayedAgentProgressTreeData() {
      if (!this.agentProgressWaveId) return this.agentProgressTreeData
      const target = Number(this.agentProgressWaveId)
      const mapNode = (n) => {
        const wp = Array.isArray(n.waveProgress) ? n.waveProgress.find(x => Number(x.waveId) === target) : null
        const next = {
          ...n,
          assignedCount: wp ? (wp.assignedCount || 0) : 0,
          completedCount: wp ? (wp.completedCount || 0) : 0,
          progress: wp ? (wp.progress || 0) : 0,
          waveProgress: wp ? [wp] : []
        }
        if (Array.isArray(n.children) && n.children.length) {
          next.children = n.children.map(mapNode).filter(x => x.assignedCount > 0 || (x.children && x.children.length > 0))
        } else {
          next.children = []
        }
        return next
      }
      return (this.agentProgressTreeData || []).map(mapNode).filter(x => x.assignedCount > 0 || (x.children && x.children.length > 0))
    },
    agentProgressSummary() {
      const rows = this.displayedAgentProgressTreeData || []
      const assignedCount = rows.reduce((sum, r) => sum + (r.assignedCount || 0), 0)
      const completedCount = rows.reduce((sum, r) => sum + (r.completedCount || 0), 0)
      const pendingCount = Math.max(0, assignedCount - completedCount)
      const progress = assignedCount > 0 ? Math.round(completedCount / assignedCount * 100) : 0
      const agentCount = rows.length
      let storeCount = 0
      const walk = (nodes = []) => {
        nodes.forEach(n => {
          if (n.nodeType === 'store') storeCount += 1
          if (Array.isArray(n.children) && n.children.length) walk(n.children)
        })
      }
      walk(rows)
      return { assignedCount, completedCount, pendingCount, progress, agentCount, storeCount }
    },
    displayedWaveStats() {
      const list = this.agentProgressWaveStats || []
      if (!this.agentProgressWaveId) return list
      return list.filter(w => Number(w.waveId) === Number(this.agentProgressWaveId))
    },
    filteredRegionData() {
      let data = this.regionData
      if (this.filterDaquId) data = data.filter(d => d.daquId === this.filterDaquId)
      const hasDealerFilter = this.filterDealerCodes && this.filterDealerCodes.length > 0
      if (!this.filterXiaoquId && !this.filterPhaseStatus && !hasDealerFilter) return data
      return data.map(d => {
        let xiaoqus = d.xiaoqus || []
        if (this.filterXiaoquId) xiaoqus = xiaoqus.filter(xq => xq.xiaoquId === this.filterXiaoquId)
        if (hasDealerFilter) {
          xiaoqus = xiaoqus.map(xq => ({
            ...xq,
            dealers: (xq.dealers || []).filter(dl => this.filterDealerCodes.includes(dl.dealerCode))
          })).filter(xq => xq.dealers.length > 0)
        }
        if (this.filterPhaseStatus) {
          xiaoqus = xiaoqus.map(xq => ({
            ...xq,
            dealers: (xq.dealers || []).filter(dl => (dl.phases || []).some(p => p.status === this.filterPhaseStatus))
          })).filter(xq => xq.dealers.length > 0)
        }
        if (xiaoqus.length === 0) return null
        return { ...d, xiaoqus, dealerCount: xiaoqus.reduce((s, xq) => s + (xq.dealers || []).length, 0) }
      }).filter(Boolean)
    },
    projectStatus() {
      return this.project.status ?? this.project.Status ?? 0
    },
    projectTimeRange() {
      const start = this.project.startTime || this.project.start_time || this.project.StartTime
      const end = this.project.endTime || this.project.end_time || this.project.EndTime
      if (!start && !end) return '-'
      const fmt = d => d ? new Date(d).toLocaleDateString('zh-CN') : ''
      return `${fmt(start)} 至 ${fmt(end)}`
    },
    projectStartTime() {
      const s = this.project.startTime || this.project.start_time || this.project.StartTime
      return s ? new Date(s).toLocaleDateString('zh-CN') : '-'
    },
    projectEndTime() {
      const e = this.project.endTime || this.project.end_time || this.project.EndTime
      return e ? new Date(e).toLocaleDateString('zh-CN') : '-'
    },
    // 成员统计（按实际角色分组）
    memberStats() {
      const roleLabels = {
        admin: '管理员', RD: '研究', QC: 'QC', Ops: '执行',
        Agent: '代理', Factory: '厂家', Dealer: '店端', viewer: '查看者', member: '成员'
      }
      const counts = {}
      this.members.forEach(m => {
        const role = m.Role || m.role || 'member'
        counts[role] = (counts[role] || 0) + 1
      })
      return Object.entries(counts).map(([role, count]) => ({
        role, count, label: roleLabels[role] || role
      }))
    },
    displayMembers() {
      return this.members.slice(0, 5)
    },
    dealerStats() {
      let total = 0, completed = 0
      this.waves.forEach(w => {
        total += w.totalExpected || 0
        completed += w.approvedCount || 0
      })
      return { total, completed }
    },
    auditSummary() {
      let approved = 0, pending = 0, rejected = 0
      this.waves.forEach(w => {
        (w.phaseStats || []).forEach(ps => {
          approved += ps.auditReview1 || 0
          pending += ps.auditPending || 0
          // 异常 = 提交 - 通过 - 待审核（如被驳回的）
          const abnormal = (ps.submittedCount || 0) - (ps.auditReview1 || 0) - (ps.auditPending || 0)
          if (abnormal > 0) rejected += abnormal
        })
      })
      return { approved, pending, rejected }
    },
    reviewWaves() {
      return this.waves.filter(w => w.reviewTotal > 0)
    },
    hasAuditData() {
      return this.waves.length > 0
    },
    riskData() {
      return this.calculateOverdueRisk(this.project, this.dealerStats.total, this.dealerStats.completed)
    },
    progressColor() {
      if (this.riskData.overdueRisk === 'high') return '#f56c6c'
      if (this.riskData.overdueRisk === 'medium') return '#e6a23c'
      return '#67c23a'
    },
    riskIcon() {
      const map = { low: 'el-icon-success', medium: 'el-icon-warning', high: 'el-icon-error', expired: 'el-icon-remove', completed: 'el-icon-success' }
      return map[this.riskData.overdueRisk] || 'el-icon-info'
    },
    riskLabel() {
      const map = { low: '进度正常', medium: '进度略滞后', high: '进度严重滞后', expired: '已超期', completed: '已完成' }
      return map[this.riskData.overdueRisk] || '未知'
    }
  },

  mounted() {
    this.loadMyProjectScope()
    this.loadData()
  },
  watch: {
    projectId: {
      immediate: false,
      handler() {
        this.loadMyProjectScope()
        this.loadData()
      }
    }
  },

  methods: {
    // 文档 51 P2-01：拉取项目级角色与可见性范围
    async loadMyProjectScope() {
      if (!this.projectId) return
      try {
        const res = await this.axios.get(`/ProjectManager/project/${this.projectId}/myScope`)
        const data = res?.data?.data || res?.data || res || {}
        this.myScope = data && (data.role !== undefined) ? data : null
      } catch { /* 失败时保持 myScope=null，不展示项目级敏感视图 */ }
    },

    async loadData() {
      if (!this.projectId) return
      this.loading = true
      try {
        const [projectRes, quotaRes, membersRes, wavesRes] = await Promise.all([
          getProjectDetail(this.projectId).catch(e => { console.error('getProjectDetail error:', e); return {} }),
          getProjectQuota(this.projectId).catch(() => ({})),
          getProjectMembers({ ProjectId: parseInt(this.projectId) }).catch(() => ({})),
          getWaveList(this.projectId).catch(() => ({}))
        ])

        const projectData = projectRes?.data?.data || projectRes?.data || projectRes || {}
        this.project = projectData

        const quotaList = quotaRes?.data?.data || quotaRes?.data || []
        this.quota = {
          total: quotaList.reduce((s, q) => s + (q.target || 0), 0),
          completed: quotaList.reduce((s, q) => s + (q.completed || 0), 0),
          pending: 0, rate: 0
        }
        this.quota.pending = this.quota.total - this.quota.completed
        this.quota.rate = this.quota.total > 0 ? Math.round(this.quota.completed / this.quota.total * 100) : 0

        this.members = membersRes?.data?.data || membersRes?.data || []

        const wavesData = wavesRes?.data?.data || wavesRes?.data || {}
        const waveRows = wavesData?.rows || wavesData?.Rows || []
        this.waves = waveRows.map(w => {
          const today = new Date()
          const endTime = w.EndTime || w.endTime
          const endDate = endTime ? new Date(endTime) : null
          const totalExpected = w.TotalExpected || w.totalExpected || 0
          const approvedCount = w.TotalApproved || w.totalApproved || 0
          const completedCount = w.CompletedCount || w.completedCount || 0
          return {
            id: w.Id || w.id,
            name: w.Name || w.name,
            dealerTotal: w.DealerCount || w.dealerCount || 0,
            completedCount,
            totalExpected, approvedCount,
            phaseCount: (w.Phases || w.phases || []).length,
            percentage: totalExpected > 0 ? Math.min(100, Math.round(approvedCount / totalExpected * 100)) : 0,
            isOverdue: endDate && today > endDate && approvedCount < totalExpected,
            remainingDays: endDate ? Math.max(0, Math.round((endDate - today) / (1000 * 60 * 60 * 24))) : null,
            reviewTotal: w.ReviewTotal || w.reviewTotal || 0,
            reviewPending: w.ReviewPending || w.reviewPending || 0,
            review1: w.Review1 || w.review1 || 0,
            review2: w.Review2 || w.review2 || 0,
            review3: w.Review3 || w.review3 || 0,
            reviewCompleted: w.ReviewCompleted || w.reviewCompleted || 0,
            phaseStats: (w.PhaseStats || w.phaseStats || []).map(ps => ({
              phaseNo: ps.PhaseNo || ps.phaseNo,
              phaseName: ps.PhaseName || ps.phaseName,
              formType: ps.FormType || ps.formType || 0,
              dealerCount: ps.DealerCount || ps.dealerCount || 0,
              submittedCount: ps.SubmittedCount || ps.submittedCount || 0,
              approvedCount: ps.ApprovedCount || ps.approvedCount || 0,
              auditPending: ps.AuditPending || ps.auditPending || 0,
              auditReview1: ps.AuditReview1 || ps.auditReview1 || 0,
              auditReview2: ps.AuditReview2 || ps.auditReview2 || 0,
              auditReview3: ps.AuditReview3 || ps.auditReview3 || 0
            }))
          }
        })

        // QC/Ops 角色：自动选中第一个波次并加载地区数据
        if ((this.isQcRole || this.isOpsRole) && this.waves.length > 0 && !this.selectedWaveId) {
          this.selectedWaveId = this.waves[0].id
          this.$nextTick(() => this.loadRegionData())
        }

        // v1.5 修复 P1-01：Ops 概览的代理进度块也要默认选中一个波次，
        // 否则首次加载会查"全部波次"，与界面顶部的波次选择框语义不一致。
        if (this.isOpsRole && this.waves.length > 0 && !this.agentProgressWaveId) {
          // 取最近开始的波次（最新一期），后端 BuildWaveWindows 已正确处理 EndTime 兜底
          this.agentProgressWaveId = this.waves[0].id
        }

        if (this.isOpsRole) {
          await this.loadAgentHierarchyProgress()
        }
      } catch (e) {
        console.error('加载概览数据失败', e)
      } finally {
        this.loading = false
      }
    },

    calculateOverdueRisk(project, totalQuota, completedQuota) {
      const today = new Date()
      const startTimeStr = project.start_time || project.startTime
      const endTimeStr = project.end_time || project.endTime
      const targetQuota = project.target_quota || project.targetQuota
      const effectiveTotal = targetQuota || totalQuota
      const startDate = startTimeStr ? new Date(startTimeStr) : new Date()
      const endDate = endTimeStr ? new Date(endTimeStr) : new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)
      const totalDays = Math.max(1, (endDate - startDate) / (1000 * 60 * 60 * 24))
      const elapsedDays = Math.max(1, (today - startDate) / (1000 * 60 * 60 * 24))
      const remainingDays = Math.max(0, Math.round((endDate - today) / (1000 * 60 * 60 * 24)))
      const remainingQuota = effectiveTotal - completedQuota
      const dailyAverage = completedQuota / elapsedDays
      const requiredDailyAverage = remainingDays > 0 ? remainingQuota / remainingDays : Infinity
      let predictedDaysToFinish = dailyAverage > 0 ? remainingQuota / dailyAverage : Infinity
      let overdueRate = 0, overdueRisk = 'low'
      if (remainingDays <= 0) {
        overdueRate = completedQuota >= effectiveTotal ? 0 : 100
        overdueRisk = completedQuota >= effectiveTotal ? 'completed' : 'expired'
      } else if (dailyAverage <= 0 && remainingQuota > 0) {
        overdueRate = 100; overdueRisk = 'high'
      } else if (effectiveTotal > 0) {
        const gap = predictedDaysToFinish - remainingDays
        if (gap <= 0) { overdueRate = 0; overdueRisk = 'low' }
        else {
          overdueRate = Math.min(100, Math.round((gap / remainingDays) * 100))
          overdueRisk = overdueRate >= 50 ? 'high' : overdueRate >= 20 ? 'medium' : 'low'
        }
      }
      const speedUpRatio = dailyAverage > 0 ? Math.round(requiredDailyAverage / dailyAverage * 10) / 10 : Infinity
      return {
        totalDays: Math.round(totalDays), elapsedDays: Math.round(elapsedDays), remainingDays,
        completionRate: effectiveTotal > 0 ? Math.round(completedQuota / effectiveTotal * 100) : 0,
        dailyAverage: Math.round(dailyAverage * 10) / 10,
        requiredDailyAverage: Math.round(requiredDailyAverage * 10) / 10,
        overdueRate, overdueRisk, speedUpRatio, needSpeedUp: speedUpRatio > 1
      }
    },

    getInitial(name) {
      return name ? name.charAt(0).toUpperCase() : '?'
    },

    // === QC/Ops 地区视图 ===
    async loadRegionData() {
      if (!this.selectedWaveId) { this.regionData = []; this.regionSummary = null; return }
      this.regionLoading = true
      try {
        const viewType = this.isQcRole ? 'qc' : 'ops'
        const res = await getOverviewByRegion(this.selectedWaveId, viewType)
        const payload = res?.data?.data || res?.data || {}
        // 兼容新格式 {summary, regions} 和旧格式(纯数组)
        if (Array.isArray(payload)) {
          this.regionData = payload
          this.regionSummary = null
        } else {
          this.regionData = payload.regions || payload.Regions || []
          const s = payload.summary || payload.Summary || null
          this.regionSummary = s ? {
            totalDispatched: s.TotalDispatched ?? s.totalDispatched ?? 0,
            totalSubmitted: s.TotalSubmitted ?? s.totalSubmitted ?? 0,
            totalApproved: s.TotalApproved ?? s.totalApproved ?? 0,
            totalMissing: s.TotalMissing ?? s.totalMissing ?? 0,
            todaySubmitted: s.TodaySubmitted ?? s.todaySubmitted ?? 0,
            yesterdaySubmitted: s.YesterdaySubmitted ?? s.yesterdaySubmitted ?? 0
          } : null
        }
        // 默认展开所有大区
        this.expandedDaqus = this.regionData.map(d => d.daquId)
      } catch (e) {
        console.error('加载地区数据失败', e)
        const data = e?.data?.data || e?.data || []
        if (Array.isArray(data) && data.length > 0) this.regionData = data
      } finally { this.regionLoading = false }
    },
    toggleDaqu(id) {
      const idx = this.expandedDaqus.indexOf(id)
      if (idx >= 0) this.expandedDaqus.splice(idx, 1)
      else this.expandedDaqus.push(id)
    },
    toggleXiaoqu(key) {
      const idx = this.expandedXiaoqus.indexOf(key)
      if (idx >= 0) this.expandedXiaoqus.splice(idx, 1)
      else this.expandedXiaoqus.push(key)
    },
    isDaquOpen(id) { return this.expandedDaqus.includes(id) },
    isXiaoquOpen(key) { return this.expandedXiaoqus.includes(key) },
    phaseChipClass(p) {
      if (p.status === 'approved' || p.auditStatus >= 3) return 'chip-success'
      if (p.auditStatus === -1) return 'chip-danger'
      if (p.status === 'submitted') return 'chip-warning'
      if (p.status === 'in_progress') return 'chip-blue'
      return 'chip-gray'
    },
    getPhaseTagClass(ps) {
      const s = ps.Status || ps.status || ''
      const map = { 'not_started': 'phase-gray', 'in_progress': 'phase-blue', 'submitted': 'phase-green', 'reviewing': 'phase-orange', 'rejected': 'phase-red', 'approved': 'phase-green' }
      return map[s] || 'phase-gray'
    },
    handleAgentAgentChange() {
      // 切换代理：保留省/市/门店/关键字，仅缩小到该代理的范围。
      // 之所以不清下级：用户常用的场景是"先按地区找一遍 → 再换一个代理同样地区比对"。
      this.loadAgentHierarchyProgress()
    },
    handleAgentProvinceChange() {
      this.agentProgressFilters.city = ''
      this.agentProgressFilters.storeCode = ''
      this.loadAgentHierarchyProgress()
    },
    handleAgentCityChange() {
      this.agentProgressFilters.storeCode = ''
      this.loadAgentHierarchyProgress()
    },
    /**
     * v1.5 修复 P1-03：切换波次时清空下级筛选（省/市/门店/关键字），
     * 避免不同波次门店集合不同导致筛选条件残留为空结果。
     * 代理过滤同样清空：不同波次代理归属可能不同，残留 agentId 会得到空结果。
     */
    handleAgentWaveChange() {
      this.agentProgressFilters.agentId = null
      this.agentProgressFilters.province = ''
      this.agentProgressFilters.city = ''
      this.agentProgressFilters.storeCode = ''
      this.agentProgressFilters.storeKeyword = ''
      this.loadAgentHierarchyProgress()
    },
    async loadAgentHierarchyProgress() {
      if (!this.projectId) return
      // v1.5 修复 P1-02：用请求序号防止快速切换波次时旧响应覆盖新响应。
      this._agentProgressSeq = (this._agentProgressSeq || 0) + 1
      const seq = this._agentProgressSeq

      this.agentProgressLoading = true
      // 切换前先清空，避免视觉上"先显示旧波次再被新波次覆盖"
      this.agentProgressTreeData = []
      this.agentProgressWaveStats = []

      try {
        const pickedAgentId = this.agentProgressFilters.agentId
          ? Number(this.agentProgressFilters.agentId)
          : null
        const res = await getAgentHierarchyProgress({
          projectId: Number(this.projectId),
          waveId: this.agentProgressWaveId ? Number(this.agentProgressWaveId) : null,
          agentUserId: pickedAgentId,
          province: this.agentProgressFilters.province || '',
          city: this.agentProgressFilters.city || '',
          storeCode: this.agentProgressFilters.storeCode || '',
          storeKeyword: this.agentProgressFilters.storeKeyword || ''
        })
        if (seq !== this._agentProgressSeq) return // 已有更新的请求在路上，丢弃旧响应
        const payload = res?.data?.data || res?.data || {}
        this.agentProgressTreeData = payload.records || []
        this.agentProgressProvinces = payload.provinces || []
        this.agentProgressCities = payload.cities || []
        this.agentProgressWaveStats = payload.waveStats || []
        // 仅在"无代理过滤"的请求里刷新代理下拉选项；
        // 否则选了某个代理后 records 只会剩 1 条，下拉就只剩这一个，无法切换。
        if (!pickedAgentId) {
          this.agentProgressAgentOptions = (payload.records || [])
            .filter(r => r && (r.nodeType === 'agent' || r.nodeType === undefined))
            .map(r => {
              // id 形如 "agent-{uid}"
              const m = typeof r.id === 'string' ? r.id.match(/^agent-(\d+)$/) : null
              const uid = m ? Number(m[1]) : null
              return uid ? { uid, name: r.name || r.username || `代理${uid}` } : null
            })
            .filter(Boolean)
        }
      } catch (e) {
        if (seq === this._agentProgressSeq) {
          this.agentProgressTreeData = []
        }
      } finally {
        if (seq === this._agentProgressSeq) {
          this.agentProgressLoading = false
        }
      }
    },
    exportAgentHierarchyProgress() {
      const selectedWave = this.agentProgressWaveId
        ? this.waves.find(w => Number(w.id) === Number(this.agentProgressWaveId))
        : null
      const scopeText = selectedWave ? `当前波次【${selectedWave.name}】` : '全部波次'

      this.$confirm(`将导出${scopeText}的代理进度数据，是否继续？`, '导出确认', {
        confirmButtonText: '导出',
        cancelButtonText: '取消',
        type: 'info'
      }).then(() => {
        const rows = []
        const walk = (nodes = [], path = { agent: '', province: '', city: '' }) => {
          nodes.forEach(n => {
            const nextPath = { ...path }
            if (n.nodeType === 'agent') nextPath.agent = n.name
            if (n.nodeType === 'province') nextPath.province = n.name
            if (n.nodeType === 'city') nextPath.city = n.name
            if (n.nodeType === 'store') {
              const base = {
                '代理账号': nextPath.agent,
                '负责省份': n.province || nextPath.province,
                '负责城市': n.city || nextPath.city,
                '门店全称': n.name,
                '门店编码': n.storeCode || ''
              }
              const rawWaveList = n.waveProgress || []
              const waveList = this.agentProgressWaveId
                ? rawWaveList.filter(wp => Number(wp.waveId) === Number(this.agentProgressWaveId))
                : rawWaveList
              if (!waveList.length) {
                rows.push({
                  ...base,
                  '波次': this.agentProgressWaveId ? '（当前筛选波次无任务）' : '-',
                  '分配任务': n.assignedCount || 0,
                  '已完成数': n.completedCount || 0,
                  '进度%': `${n.progress || 0}%`
                })
              } else {
                waveList.forEach(wp => {
                  rows.push({
                    ...base,
                    '波次': wp.waveName,
                    '分配任务': wp.assignedCount || 0,
                    '已完成数': wp.completedCount || 0,
                    '进度%': `${wp.progress || 0}%`
                  })
                })
              }
            }
            if (Array.isArray(n.children) && n.children.length) walk(n.children, nextPath)
          })
        }
        walk(this.displayedAgentProgressTreeData)
        if (!rows.length) {
          this.$message.warning('暂无可导出数据')
          return
        }
        const ws = XLSX.utils.json_to_sheet(rows)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, '代理进度')
        const waveName = selectedWave ? `_${(selectedWave.name || '').replace(/[\\/:*?"<>|]/g, '_')}` : '_全部波次'
        XLSX.writeFile(wb, `代理进度${waveName}_${Date.now()}.xlsx`)
      }).catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
.project-overview {
  padding: 0;
}

/* ===== 通用布局 ===== */
.row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.card {
  flex: 1;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  min-width: 0;

  &.full-width { flex: none; width: 100%; }
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 18px 22px;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;

  .header-icon {
    font-size: 18px;
    &.blue   { color: #4f8ff7; }
    &.green  { color: #10b981; }
    &.purple { color: #8b5cf6; }
    &.orange { color: #f59e0b; }
  }

  .count-badge {
    margin-left: auto;
    padding: 2px 10px;
    background: #f1f5f9;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
  }
}

.card-body {
  padding: 4px 22px 22px;
}

/* ===== 项目信息 ===== */
.info-item {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
  &:last-child { margin-bottom: 0; }
  label {
    width: 80px;
    color: #94a3b8;
    font-size: 13px;
    flex-shrink: 0;
  }
  .value {
    flex: 1;
    color: #1e293b;
    font-size: 14px;
    font-weight: 500;
    text-align: right;
  }
  .mono {
    font-family: 'SF Mono', 'Cascadia Mono', monospace;
    letter-spacing: 0.5px;
  }
}

.status-tag {
  float: right;
  border-radius: 6px;
  font-weight: 500;
}

/* ===== 项目进度 ===== */
.progress-main {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
}

.progress-big-number {
  font-size: 32px;
  font-weight: 700;
  color: #10b981;
  min-width: 80px;
  line-height: 1;
  &.text-high, &.text-expired { color: #f56c6c; }
  &.text-medium { color: #e6a23c; }
  &.text-low { color: #10b981; }
  &.text-completed { color: #409eff; }
}

.progress-bar {
  flex: 1;
}

.time-row {
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
}

.time-cell {
  label {
    display: block;
    color: #94a3b8;
    font-size: 12px;
    margin-bottom: 4px;
  }
  span {
    color: #1e293b;
    font-size: 14px;
  }
}

.text-danger {
  color: #f56c6c !important;
}

.risk-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;

  &.risk-low, &.risk-completed { background: #ecfdf5; color: #10b981; }
  &.risk-medium { background: #fffbeb; color: #d97706; }
  &.risk-high, &.risk-expired { background: #fef2f2; color: #ef4444; }

  .risk-divider { opacity: 0.3; }
  .risk-speed { opacity: 0.8; }
}

/* ===== 配额完成度 ===== */
.quota-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.quota-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #f1f5f9;
}

.quota-left {
  .quota-label {
    font-size: 13px;
    color: #94a3b8;
    margin-bottom: 6px;
  }
  .quota-value {
    font-size: 28px;
    font-weight: 700;
    color: #1e293b;
    line-height: 1;
  }
}

.quota-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

/* ===== 审核概览卡片 ===== */
.audit-summary-row {
  display: flex;
  gap: 16px;
}

.audit-card {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fff;
  border-radius: 14px;
  padding: 20px 24px;
  border: 1px solid #e2e8f0;
}

.audit-card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.audit-card-info {
  .audit-card-label {
    font-size: 13px;
    color: #94a3b8;
    margin-bottom: 4px;
  }
  .audit-card-value {
    font-size: 28px;
    font-weight: 700;
    color: #1e293b;
    line-height: 1;
    .unit {
      font-size: 14px;
      font-weight: 400;
      color: #94a3b8;
      margin-left: 4px;
    }
  }
}

/* ===== 成员卡片 ===== */
.role-stats {
  display: flex;
  gap: 28px;
  margin-bottom: 18px;
}

.role-item {
  text-align: center;
  .role-count {
    display: block;
    font-size: 26px;
    font-weight: 700;
    color: #1e293b;
    line-height: 1.2;
  }
  .role-label {
    font-size: 12px;
    color: #94a3b8;
  }
}

.member-avatars {
  display: flex;
  gap: 8px;
}

.avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;

  &.more {
    background: #e2e8f0;
    color: #64748b;
    font-size: 12px;
  }
}

/* ===== 波次回收进度（分阶段） ===== */
.wave-list {
  min-height: 80px;
}

.wave-block {
  background: #f8fafc;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 12px;
  &:last-child { margin-bottom: 0; }
}

.wave-block-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  .wave-name {
    font-size: 14px;
    font-weight: 600;
    color: #334155;
  }
  .wave-summary {
    font-size: 12px;
    color: #94a3b8;
  }
}

.phase-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.phase-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.phase-info {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 140px;
  flex-shrink: 0;
}

.phase-no {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.phase-name {
  font-size: 12px;
  color: #475569;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 110px;
}

.phase-progress {
  flex: 1;
}

.phase-stats {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  min-width: 40px;
  text-align: right;
}

.empty-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
  border: 2px dashed #e2e8f0;
  border-radius: 10px;
  color: #94a3b8;

  i { font-size: 32px; margin-bottom: 8px; }
  p { margin: 0; font-size: 13px; }
}

/* ===== 数据审核进度 ===== */
.review-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.review-block {
  border-radius: 12px;
  padding: 28px 32px;
}

.review-block-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
  .wave-name { font-weight: 700; color: #1e293b; font-size: 16px; }
  .wave-total { font-size: 13px; color: #94a3b8; }
}

.review-block-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
}

.view-report {
  font-size: 13px;
  color: #409eff;
  cursor: pointer;
  &:hover { color: #337ecc; }
  i { margin-left: 2px; font-size: 12px; }
}

.review-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.review-item {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 28px;
  .review-label { width: 50px; font-size: 13px; color: #94a3b8; flex-shrink: 0; }
  .review-progress { flex: 1; }
  .review-pct { font-size: 13px; color: #64748b; width: 40px; text-align: right; flex-shrink: 0; }
  .review-num { font-size: 13px; color: #94a3b8; white-space: nowrap; min-width: 40px; text-align: right; flex-shrink: 0; }
}

.phase-audit-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.phase-audit-block {
  padding: 20px 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
  &:first-child { padding-top: 0; }
}

.phase-audit-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.phase-no-lg {
  font-size: 15px;
  font-weight: 600;
  color: #64748b;
}

.phase-audit-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

/* ===== QC/Ops 地区视图 ===== */
.region-view {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.04);
  overflow: hidden;
}
.region-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 22px;
  border-bottom: 1px solid #f1f5f9;
  flex-wrap: wrap;
  gap: 8px;
  h3 { margin: 0; font-size: 16px; font-weight: 600; color: #1e293b; display: flex; align-items: center; }
}
.region-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.region-tree {
  padding: 8px 0;
  min-height: 200px;
}
.region-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 22px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #f8fafc; }
}
.daqu-row {
  font-weight: 600;
  font-size: 15px;
  color: #1e293b;
  border-bottom: 1px solid #f1f5f9;
}
.xiaoqu-row {
  padding-left: 44px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}
.expand-icon {
  font-size: 12px;
  color: #9ca3af;
  transition: transform 0.2s ease;
  flex-shrink: 0;
  &.open { transform: rotate(90deg); }
}
.region-name { flex: 1; }
.region-stat {
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
  padding: 2px 8px;
  background: #f3f4f6;
  border-radius: 6px;
  &.success { background: #ecfdf5; color: #059669; }
  &.primary { background: #eff6ff; color: #3b82f6; }
}
.dealer-list {
  padding: 4px 22px 12px 56px;
}
.dealer-table {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  ::v-deep th { background: #f9fafb !important; font-size: 12px; font-weight: 500; color: #6b7280; padding: 8px 0; }
  ::v-deep td { font-size: 13px; color: #111827; padding: 8px 0; }
  ::v-deep .el-table__body tr:hover > td { background: #f9fafb !important; }
}
.phase-chip {
  display: inline-block;
  padding: 2px 8px;
  margin: 1px 3px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  &.chip-success { background: #ecfdf5; color: #059669; }
  &.chip-danger { background: #fef2f2; color: #dc2626; }
  &.chip-warning { background: #fffbeb; color: #d97706; }
  &.chip-blue { background: #eff6ff; color: #3b82f6; }
  &.chip-gray { background: #f3f4f6; color: #9ca3af; }
}
/* 阶段状态标签 - 与波次管理一致 */
.phase-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}
.phase-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.phase-gray { background: #f3f4f6; color: #6b7280; }
.phase-blue { background: #dbeafe; color: #2563eb; }
.phase-green { background: #d1fae5; color: #059669; }
.phase-orange { background: #fef3c7; color: #d97706; }
.phase-red { background: #fee2e2; color: #dc2626; }

.wave-stats-panel {
  margin: 10px 22px 12px;
  padding: 12px;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  background: #fff;
}
.wave-stats-title {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 10px;
}
.agent-hierarchy-panel {
  margin: 10px 22px 22px;
  padding: 12px;
  border: 1px solid #eef2f7;
  border-radius: 10px;
}
.agent-hierarchy-toolbar {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.ops-toolbar-btn {
  height: 30px;
  padding: 0 16px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #d1d5db;
  color: #374151;
  background: #fff;
  transition: all .15s ease;

  &:hover,
  &:focus {
    color: #2563eb;
    border-color: #2563eb;
    background: #fff;
  }

  &.ops-toolbar-btn--primary {
    border-color: #2563eb;
    background: #2563eb;
    color: #fff;

    &:hover,
    &:focus {
      border-color: #1d4ed8;
      background: #1d4ed8;
      color: #fff;
    }
  }
}

::v-deep .agent-hierarchy-toolbar .el-button--small {
  padding: 0 16px;
  min-width: 72px;
  border-radius: 9999px;
}
.wave-progress-chips {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.wave-progress-chip {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  font-size: 12px;
  color: #334155;
  .wave-name {
    max-width: 130px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #64748b;
  }
  .wave-num {
    color: #2563eb;
  }
  .wave-pct {
    color: #059669;
    font-weight: 600;
  }
}

/* ===== 汇总统计卡片 ===== */
.summary-cards {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.summary-card {
  flex: 1;
  min-width: 140px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
}
.summary-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}
.summary-info {
  .summary-label {
    font-size: 12px;
    color: #94a3b8;
    margin-bottom: 2px;
  }
  .summary-value {
    font-size: 22px;
    font-weight: 700;
    color: #1e293b;
    .unit {
      font-size: 12px;
      font-weight: 400;
      color: #94a3b8;
      margin-left: 2px;
    }
  }
}
</style>
